import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import {
  RespondentCategoryDto,
  RespondentResponseSubmitDto,
  RespondentSurveyDto
} from './models';
import { RespondentSurveyService } from './services/respondent-survey.service';

/** Where the respondent is in the survey. */
type RespondentState = 'not-started' | 'in-progress' | 'completed' | 'error';

/**
 * The survey as the respondent answers it.
 *
 * Reached from the invitation link at
 * `/work-environment-survey/respondent/:respondentId`, outside the
 * authenticated app: the person answering is an employee of the customer, not
 * necessarily a Kuba user, so this page has no `AuthGuard`, no navigation and
 * no session. It follows `sds-public-preview.component`, the other public page
 * in the app, which is likewise declared in its feature module and routed from
 * `app.routing.ts`.
 *
 * Ported from the AMIL `RespondentSurvey` page together with the
 * `SurveyHomePage`, `SurveyCard`, `SurveyCategory`, `SurveyProgression` and
 * `SurveyCompleted` components it composed — one screen with three states
 * rather than five components passing a survey object between them. The
 * confetti on completion is not carried over.
 */
@Component({
  selector: 'respondent-survey',
  templateUrl: './respondent-survey.component.html',
  styleUrls: ['./respondent-survey.component.scss'],
  standalone: false
})
export class RespondentSurveyComponent implements OnInit, OnDestroy {
  //#region Variables
  /** The five answer values every question is scored on. */
  readonly answerValues = [1, 2, 3, 4, 5];

  state: RespondentState = 'not-started';
  survey: RespondentSurveyDto | null = null;
  categoryIndex = 0;

  isLoading = false;
  isSaving = false;

  private respondentId = '';
  private subscriptions: Subscription[] = [];
  //#endregion

  constructor(
    private route: ActivatedRoute,
    private respondentSurveyService: RespondentSurveyService
  ) {}

  //#region Lifecycle
  ngOnInit(): void {
    this.respondentId = (
      this.route.snapshot.paramMap.get('respondentId') || ''
    ).trim();

    if (!this.respondentId) {
      this.state = 'error';
      return;
    }

    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
  //#endregion

  //#region State
  get currentCategory(): RespondentCategoryDto | null {
    return this.survey?.Categories[this.categoryIndex] ?? null;
  }

  get categoryCount(): number {
    return this.survey?.Categories.length ?? 0;
  }

  get isLastCategory(): boolean {
    return this.categoryIndex === this.categoryCount - 1;
  }

  /** Percentage of all questions answered, across every category. */
  get progressPercent(): number {
    if (this.survey === null) {
      return 0;
    }

    let total = 0;
    let answered = 0;

    for (const category of this.survey.Categories) {
      total += category.Questions.length;
      answered += category.Questions.filter(
        question => question.Response !== null
      ).length;
    }

    return total === 0 ? 0 : Math.round((answered / total) * 100);
  }

  get isCurrentCategoryComplete(): boolean {
    const category = this.currentCategory;

    if (category === null) {
      return false;
    }

    return category.Questions.every(question => question.Response !== null);
  }
  //#endregion

  //#region Answering
  start(): void {
    this.state = 'in-progress';
  }

  /**
   * `p-radioButton` and native radios both hand back a string, so the value is
   * normalised here rather than in every template binding.
   */
  setAnswer(questionId: number, value: number | string): void {
    const category = this.currentCategory;

    if (category === null) {
      return;
    }

    const question = category.Questions.find(item => item.Id === questionId);

    if (question !== undefined) {
      question.Response = Number(value);
    }
  }

  back(): void {
    if (this.categoryIndex > 0) {
      this.categoryIndex--;
      this.scrollToTop();
    }
  }

  next(): void {
    this.saveCurrentCategory(() => {
      if (!this.isLastCategory) {
        this.categoryIndex++;
        this.scrollToTop();
      }
    });
  }

  finish(): void {
    this.saveCurrentCategory(() => {
      this.state = 'completed';
      this.scrollToTop();
    });
  }

  backToSurvey(): void {
    this.state = 'in-progress';
  }
  //#endregion

  //#region Data
  private load(): void {
    this.isLoading = true;

    this.subscriptions.push(
      this.respondentSurveyService.getSurvey(this.respondentId).subscribe({
        next: survey => {
          this.survey = survey;
          this.isLoading = false;
        },
        error: () => {
          this.state = 'error';
          this.isLoading = false;
        }
      })
    );
  }

  /**
   * Saves the answers for the category on screen, then runs `onSaved`.
   *
   * Saving per category is what lets a respondent close the tab and come back
   * to a half-finished survey. The navigation only happens once the save has
   * landed, so a failed request leaves them on the answers it did not keep.
   */
  private saveCurrentCategory(onSaved: () => void): void {
    const category = this.currentCategory;

    if (category === null || !this.isCurrentCategoryComplete || this.isSaving) {
      return;
    }

    const responses: RespondentResponseSubmitDto[] = category.Questions.map(
      question => ({
        QuestionId: question.Id,
        Response: question.Response as number
      })
    );

    this.isSaving = true;

    this.subscriptions.push(
      this.respondentSurveyService
        .submitCategoryResponses(this.respondentId, responses)
        .subscribe({
          next: () => {
            this.isSaving = false;
            onSaved();
          },
          error: () => {
            this.isSaving = false;
          }
        })
    );
  }
  //#endregion

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
